import { useState } from "react";
import EntityTable from "../EntityTable";
import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { Updater } from "@tanstack/table-core/build/lib/types";
import { BaseNodeEntity } from "../../types/baseNodeEntity";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

interface EntitySelectionModalProps<
  T extends BaseNodeEntity,
> {
  open: boolean;
  onClose: () => void;
  title: string;
  columns: MRT_ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  pagination: MRT_PaginationState;
  onPaginationChange: (pagination: Updater<MRT_PaginationState>) => void;
  onSelect: (selectedEntity: T | null) => void;
  totalRowCount: number;
  initialSelectedEntity?: T | null;
  required?: boolean;
  error?: string | null;
}

const EntitySelectionModal = <
  T extends BaseNodeEntity,
>({
  open,
  onClose,
  title,
  columns,
  error = null,
  data,
  loading,
  pagination,
  onPaginationChange,
  onSelect,
  totalRowCount,
  initialSelectedEntity,
  required = false,
}: EntitySelectionModalProps<T>) => {
  const [selectedEntity, setSelectedEntity] = useState<T | null>(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box>
          <Typography variant="h2">{title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <EntityTable
          loading={loading}
          error={error}
          columns={columns}
          data={data}
          pagination={pagination}
          manualPagination={true}
          onPaginationChange={onPaginationChange}
          enableRowSelection={true}
          isMultipleRowSelection={false}
          onRowSelectionChange={(selectedData) => {
            setSelectedEntity(selectedData);
          }}
          totalRowCount={totalRowCount}
          initialSelectedEntityKey={initialSelectedEntity?._key}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="contained">
          Back
        </Button>
        <Button
          onClick={() => {
            onSelect(selectedEntity);
            onClose();
          }}
          variant="contained"
          color="primary"
          disabled={required && !selectedEntity}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntitySelectionModal;
